import type { Telemetry } from "~/telemetry";
import type * as t from "~/types";
import type { State, QuizContext } from "~/quiz-store";
import type { SequenceNode } from "~/sequence";

import { derived, get, type Writable } from "svelte/store";
import { getSegmentConfig, getShopifyDiscountCodeUrl } from "~/utils";
import { EventEmitter } from "./event-emitter";

function executeHooks(hooks?: Array<() => void> | null) {
    if (hooks == null) {
        return;
    }
    hooks.forEach((h) => h());
}

export function createActions(
    state: Writable<State>,
    telemetry: Telemetry,
    eventEmitter: EventEmitter,
) {
    const beforeProceedHooks: Record<symbol, Array<() => void>> = {};
    const beforeStepEnterHooks: Record<symbol, Array<() => void>> = {};
    const onIsNextStepHooks: Record<symbol, Array<() => void>> = {};

    function handleSelectOption(option: t.MultipleChoiceOption) {
        state.update(($) => {
            if ($.step.value.type !== "multiple_choice") {
                throw new Error(
                    `Trying to set selected option in step of type  "${$.step.value.type}"`,
                );
            }
            $.step.value.state = {
                selectedOption: option,
            };
            return $;
        });
    }

    function incrementScore(option: t.MultipleChoiceOption) {
        state.update((s) => {
            if (option.answerId == null) {
                return s;
            }
            if (s.score[option.answerId] == null) {
                s.score[option.answerId] = 0;
            }
            ++s.score[option.answerId];
            return s;
        });
    }

    function startQuiz() {
        const { totalSteps } = get(state);

        telemetry.logEvent("game_start", {
            total_steps: totalSteps,
        });

        state.update(($) => {
            $.status = "started";
            $.globalStepIdx = 0;
            return $;
        });
    }

    function goToNextStep() {
        const { globalStepIdx } = get(state);

        telemetry.logEvent("step_forward", {
            step_number: globalStepIdx + 1,
        });

        state.update(($) => {
            ++$.globalStepIdx;

            const beforeHooks = beforeProceedHooks[$.step.id];
            executeHooks(beforeHooks);
            delete beforeProceedHooks[$.step.id];

            const stepEnterHooks = beforeStepEnterHooks[$.step.id];
            if (stepEnterHooks != null && stepEnterHooks.length !== 0) {
                console.log(`--- running step enter hooks`);
            }
            executeHooks(stepEnterHooks);
            delete beforeStepEnterHooks[$.step.id];

            let nextStep = $.step.next;
            if (nextStep == null) {
                const nextSegment = $.segment.next;
                if (nextSegment != null) {
                    $.segment = nextSegment;
                    nextStep = nextSegment.value.steps.head;
                }
            }

            if (nextStep != null) {
                $.step = nextStep;
            }

            return $;
        });

        eventEmitter.emit(EventEmitter.EVENT.ON_CHANGE_STEP);
    }

    function calculateResult(
        segmentNode: SequenceNode<t.Segment>,
    ): t.QuizResult | undefined {
        const { score, config } = get(state);
        let winningAnswerIds: number[] = [];
        let high = 0;
        for (const [answerIdStr, count] of Object.entries(score)) {
            const answerId = Number(answerIdStr);
            // Adds highest scoring answer to an array. If it'a a tie between
            // two or more answers, winningAnswerIds will include all of them.
            switch (true) {
                case count === high:
                    winningAnswerIds.push(answerId);
                case count > high:
                    winningAnswerIds = [answerId];
                    high = count;
            }
        }

        const winningAnswerId = winningAnswerIds[0];
        const segmentConfig = getSegmentConfig(config, segmentNode);
        if (winningAnswerId == null || winningAnswerId < 1) {
            console.error(
                "[shpln-quiz] Unable to determine winner. Winning answer id is less than 1",
                winningAnswerId,
            );
            return;
        }
        const resIdx = winningAnswerId - 1;
        const winner = segmentConfig?.results[resIdx];
        if (winner == null) {
            console.error(
                "[shpln-quiz] Unable to determine winner from score: ",
                score,
            );
            return;
        }

        let discountUrl: string | undefined;
        if (winner.discount_code != null) {
            discountUrl = getShopifyDiscountCodeUrl(
                winner.discount_code,
                winner.redirect_url,
            );
        }

        telemetry.logEvent("result", {
            answer_id: winningAnswerId,
            discount_code: winner.discount_code,
            redirect_url: winner.redirect_url,
            discount_url: discountUrl,
        });

        return {
            discountCode: winner.discount_code,
            redirectUrl: winner.redirect_url,
        };
    }

    function onBeforeProceed(
        stepNode: SequenceNode<t.Step>,
        fn: (state: Writable<State>) => void,
    ) {
        const hooks = beforeProceedHooks[stepNode.id];
        if (hooks == null) {
            beforeProceedHooks[stepNode.id] = [];
        }
        beforeProceedHooks[stepNode.id].push(() => fn(state));
    }

    function onBeforeStepEnter(
        stepNode: SequenceNode<t.Step>,
        fn: (state: Writable<State>) => void,
    ) {
        const hooks = beforeStepEnterHooks[stepNode.id];
        if (hooks == null) {
            beforeStepEnterHooks[stepNode.id] = [];
        }
        beforeStepEnterHooks[stepNode.id].push(() => fn(state));
        console.log(
            `--- before enter, will run hook for ${stepNode.value.type}`,
            beforeStepEnterHooks,
        );
    }

    function onIsNextStep(stepNode: SequenceNode<t.Step>, fn: () => void) {
        /**
         * If the step node that subscribed to this hook is either the first or
         * second step, then we execute the hook immediately because it is already
         * the "next step".
         * */
        if (stepNode.idx <= 1) {
            fn();
            return;
        }

        const hooks = onIsNextStepHooks[stepNode.id];
        if (hooks == null) {
            onIsNextStepHooks[stepNode.id] = [];
        }
        onIsNextStepHooks[stepNode.id].push(fn);
    }

    const sequenceState = derived(state, ($) => {
        if ($ == null) {
            return;
        }
        return {
            segment: $.segment,
            step: $.step,
        };
    });
    sequenceState.subscribe(($) => {
        const nextStep = $?.step.next ?? $?.segment.next?.value.steps.head;
        if (nextStep == null) {
            return;
        }

        const hooks = onIsNextStepHooks[nextStep.id];
        executeHooks(hooks);
    });

    return {
        actions: {
            handleSelectOption,
            incrementScore,
            goToNextStep,
            calculateResult,
            startQuiz,
        },
        hooks: {
            onBeforeStepEnter,
            onBeforeProceed,
            onIsNextStep,
        },
    };
}

export function trackEvents({ state, telemetry }: QuizContext) {
    const currSegmentNode = derived(state, ($) => $.segment);
    const sequenceState = derived(state, ($) => {
        if ($ == null) {
            return;
        }
        return {
            segment: $.segment,
            step: $.step,
        };
    });

    let prevSegmentId: string;
    currSegmentNode.subscribe(($) => {
        if ($ == null) {
            return;
        }
        if (prevSegmentId === $.value.id) {
            return;
        }
        telemetry.logEvent("level_start", {
            level_number: $.idx + 1,
        });
        prevSegmentId = $.value.id;
    });

    sequenceState.subscribe(($) => {
        if ($ == null) {
            return;
        }
        const { segment, step } = $;
        if (step.next == null) {
            telemetry.logEvent("level_end", {
                level_number: segment.idx + 1,
            });
        }
        if (step.next == null && segment.next == null) {
            telemetry.logEvent("game_end");
        }
    });
}
